import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TestService } from "@/services/test/testService";
import { CarTestService } from "@/services/carTest/carTestService";
import {
  CreateTestRequest,
  CreateTestResponse,
  GetAllTestsResponse,
  UpdateTestStatusRequest,
  UpdateTestRequest,
  UpdateTestResponse,
  UpdateVehicleTestImagesRequest,
} from "@/types/vehicleTest";

// TODO: Look into if we want to split up into useTestManagement instead of using disable properties.
export const useTest = (options?: {
  disableAdminQuery?: boolean;
  disableUserQuery?: boolean;
  tab?: "active" | "handled" | "archived" | "draft"; // Tab selection for admin view ('active' | 'handled' | 'draft') or 'archived' for archive pages (not a tab)
  page?: number; // Page number for pagination (default: 1)
  limit?: number; // Items per page (default: 10)
  search?: string; // Search query
  sort?: string;
}) => {
  const queryClient = useQueryClient();
  const page = options?.page ?? 1;
  const limit = options?.limit ?? 10;
  const search = options?.search;
  const sort = options?.sort;

  // Get active tests query (Admin view - pending-review + complement)
  const getActiveTestsQuery = useQuery({
    queryKey: ["admin-tests", "active", page, limit, search, sort],
    queryFn: async () => {
      // Pass multiple statuses as array - service will convert to comma-separated
      return await TestService.getAllTestsForAdmin(
        page,
        limit,
        ["pending-review", "complement", "change-requested"],
        search,
        false,
        sort,
      );
    },
    enabled:
      !options?.disableAdminQuery &&
      (options?.tab === "active" || !options?.tab),
    staleTime: 0, // Always stale - always refetch
    gcTime: 0, // No cache retention
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: false,
  });

  // Get handled tests query (Admin view - approved + rejected + active-listing)
  const getHandledTestsQuery = useQuery({
    queryKey: ["admin-tests", "handled", page, limit, search, sort],
    queryFn: async () => {
      // Pass multiple statuses as array - service will convert to comma-separated
      return await TestService.getAllTestsForAdmin(
        page,
        limit,
        ["approved", "rejected", "active-listing"],
        search,
        false,
        sort,
      );
    },
    enabled: !options?.disableAdminQuery && options?.tab === "handled",
    staleTime: 0, // Always stale - always refetch
    gcTime: 0, // No cache retention
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: false,
  });

  // Get archived tests query (Admin view)
  const getArchivedTestsQuery = useQuery({
    queryKey: ["admin-tests", "archived"],
    queryFn: async () => {
      return await TestService.getArchivedTestsForAdmin(1, 100);
    },
    enabled: !options?.disableAdminQuery && options?.tab === "archived",
    staleTime: 0, // Always stale - always refetch
    gcTime: 0, // No cache retention
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: false,
  });

  // Get draft tests query (Admin view - ONGOING status)
  const getDraftTestsQuery = useQuery({
    queryKey: ["admin-tests", "draft", page, limit, search, sort],
    queryFn: async () => {
      return await TestService.getAllTestsForAdmin(
        page,
        limit,
        "ongoing",
        search,
        false,
        sort,
      );
    },
    enabled: !options?.disableAdminQuery && options?.tab === "draft",
    staleTime: 0, // Always stale - always refetch
    gcTime: 0, // No cache retention
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: false,
  });

  // Get user's own tests query
  const getAllUserTestsQuery = useQuery({
    queryKey: ["user-tests", page, limit, search, sort],
    queryFn: async () => {
      return TestService.getAllUserTests(page, limit, false, sort);
    },
    enabled: !options?.disableUserQuery && options?.tab !== "archived", // Disable if explicitly set to true or if archived tab
    staleTime: 0, // Always stale - always refetch
    gcTime: 0, // No cache retention
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: false,
  });

  // Get user's archived tests query
  const getUserArchivedTestsQuery = useQuery({
    queryKey: ["user-tests", "archived", 1, 100],
    queryFn: ({ queryKey, signal }) => {
      const [_, __, page = 1, limit = 100] = queryKey as [
        string,
        string,
        number?,
        number?,
      ];
      return TestService.getAllUserTests(page, limit, true);
    },
    enabled: !options?.disableUserQuery && options?.tab === "archived",
    staleTime: 0, // Always stale - always refetch
    gcTime: 0, // No cache retention
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: false,
  });

  // Get test by ID query (for immediate loading)
  const useGetTestById = (testId: string) => {
    return useQuery({
      queryKey: ["test", testId],
      queryFn: () => {
        return TestService.getTestById(testId);
      },
      enabled: !!testId,
      staleTime: 0, // Always stale - always refetch
      gcTime: 0, // No cache retention
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchInterval: false,
    });
  };

  // Get test by ID mutation (for lazy loading)
  const getTestByIdMutation = useMutation({
    mutationFn: (testId: string) => TestService.getTestById(testId),
  });

  // Create test mutation
  const createTestMutation = useMutation({
    mutationFn: (testData: CreateTestRequest) =>
      TestService.createTest(testData),
    onSuccess: (response: CreateTestResponse) => {
      // Invalidate all admin queries - they'll refetch if enabled
      queryClient.invalidateQueries({ queryKey: ["admin-tests"] });
      queryClient.invalidateQueries({ queryKey: ["user-tests"] });
      // Also invalidate the specific test if we have its ID
      if (response?.id) {
        queryClient.invalidateQueries({ queryKey: ["test", response.id] });
      }
    },
  });

  // Update test status mutation
  const updateTestStatusMutation = useMutation({
    mutationFn: ({
      updateData,
      id,
    }: {
      updateData: UpdateTestStatusRequest;
      id: string;
    }) => TestService.updateTestStatus(updateData, id),
    onSuccess: (_, variables) => {
      // Invalidate all admin queries (both active and handled) - they'll refetch if enabled
      queryClient.invalidateQueries({ queryKey: ["admin-tests"] });
      queryClient.invalidateQueries({ queryKey: ["user-tests"] });
      queryClient.invalidateQueries({ queryKey: ["test", variables.id] });
    },
  });

  // Update test mutation
  const updateTestMutation = useMutation({
    mutationFn: ({
      testId,
      updateData,
    }: {
      testId: string;
      updateData: UpdateTestRequest;
    }) => TestService.updateTest(testId, updateData),
    onSuccess: (response: UpdateTestResponse, variables) => {
      // Invalidate all admin queries - they'll refetch if enabled
      queryClient.invalidateQueries({ queryKey: ["admin-tests"] });
      queryClient.invalidateQueries({ queryKey: ["user-tests"] });
      queryClient.invalidateQueries({ queryKey: ["test", variables.testId] });
    },
  });

  // Submit test mutation (for submitting draft to review)
  const submitTestMutation = useMutation({
    mutationFn: (testId: string) => TestService.submitTest(testId),
    onSuccess: (response: UpdateTestResponse, testId: string) => {
      // Invalidate all admin queries - they'll refetch if enabled
      queryClient.invalidateQueries({ queryKey: ["admin-tests"] });
      queryClient.invalidateQueries({ queryKey: ["user-tests"] });
      queryClient.invalidateQueries({ queryKey: ["test", testId] });
      // Specifically invalidate draft queries to update Utkast flik in real-time
      queryClient.invalidateQueries({ queryKey: ["admin-tests", "draft"] });
    },
  });

  // Update vehicle test images mutation (add/remove only; reorder + rotate are done in component)
  const updateVehicleTestImagesMutation = useMutation({
    mutationFn: (updateData: UpdateVehicleTestImagesRequest) =>
      TestService.updateVehicleTestImages(updateData),
    // No onSuccess invalidate here – parent/component calls invalidateTestImages(testId) after full save (incl. reorder + rotate)
  });

  /** Call after full image save flow (add/remove + reorder + rotate) so cache refetches with correct data */
  const invalidateTestImages = useCallback(
    (testId: string) => {
      queryClient.invalidateQueries({ queryKey: ["admin-tests"] });
      queryClient.invalidateQueries({ queryKey: ["user-tests"] });
      queryClient.invalidateQueries({ queryKey: ["test", testId] });
      void queryClient.refetchQueries({ queryKey: ["test", testId] });
    },
    [queryClient],
  );

  // Get vehicle basic information by LPN mutation
  const getVehicleBasicInformationByLpnMutation = useMutation({
    mutationFn: (id: string) => TestService.getVehicleBasicInformationByLpn(id),
  });

  // Get vehicle basic information by VIN mutation
  const getVehicleBasicInformationByVinMutation = useMutation({
    mutationFn: (id: string) => TestService.getVehicleBasicInformationByVin(id),
  });

  // Add car test image mutation
  const addCarTestImageMutation = useMutation({
    mutationFn: ({
      carTestId,
      category,
      file,
      isThumbnail,
      tireType,
      damageComment,
    }: {
      carTestId: string;
      category: "exterior" | "interior" | "damage" | "service" | "tires";
      file: File;
      isThumbnail?: boolean;
      tireType?: "primary" | "secondary";
      damageComment?: string;
    }) =>
      CarTestService.addCarTestImage(
        carTestId,
        category,
        file,
        isThumbnail,
        tireType,
        damageComment,
      ),
    onSuccess: (_, variables) => {
      // Invalidate the specific test to refresh images
      queryClient.invalidateQueries({
        queryKey: ["test", variables.carTestId],
      });
      queryClient.invalidateQueries({ queryKey: ["admin-tests"] });
      queryClient.invalidateQueries({ queryKey: ["user-tests"] });
    },
  });

  // Remove car test image mutation
  const removeCarTestImageMutation = useMutation({
    mutationFn: ({
      carTestId,
      imageId,
    }: {
      carTestId: string;
      imageId: string;
    }) => CarTestService.removeCarTestImage(carTestId, imageId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["test", variables.carTestId],
      });
      queryClient.invalidateQueries({ queryKey: ["admin-tests"] });
      queryClient.invalidateQueries({ queryKey: ["user-tests"] });
    },
  });

  // Archive car tests mutation
  const archiveCarTestsMutation = useMutation({
    mutationFn: (ids: string[]) =>
      TestService.archiveCarTests(ids, !options?.disableAdminQuery),
    onSuccess: () => {
      // Invalidate all admin queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ["admin-tests"] });
      queryClient.invalidateQueries({ queryKey: ["user-tests"] });
    },
  });

  // Unarchive car tests mutation
  const unarchiveCarTestsMutation = useMutation({
    mutationFn: (ids: string[]) =>
      TestService.unarchiveCarTests(ids, !options?.disableAdminQuery),
    onSuccess: () => {
      // Invalidate all admin queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ["admin-tests"] });
      queryClient.invalidateQueries({ queryKey: ["user-tests"] });
    },
  });

  // Withdraw pending review → ongoing so the user can edit and resubmit
  const withdrawPendingReviewMutation = useMutation({
    mutationFn: (testId: string) => TestService.withdrawPendingReview(testId),
    onSuccess: (_, testId) => {
      queryClient.invalidateQueries({ queryKey: ["admin-tests"] });
      queryClient.invalidateQueries({ queryKey: ["user-tests"] });
      queryClient.invalidateQueries({ queryKey: ["test", testId] });
    },
  });

  // Request change on a submitted test
  const requestTestChangeMutation = useMutation({
    mutationFn: ({
      testId,
      message,
    }: {
      testId: string;
      message: string;
    }) => TestService.requestTestChange(testId, message),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["admin-tests"] });
      queryClient.invalidateQueries({ queryKey: ["user-tests"] });
      queryClient.invalidateQueries({ queryKey: ["test", variables.testId] });
    },
  });

  // Determine which query data to use based on tab (default to active if no tab specified)
  const activeQuery =
    options?.tab === "handled"
      ? getHandledTestsQuery
      : options?.tab === "archived"
        ? getArchivedTestsQuery
        : options?.tab === "draft"
          ? getDraftTestsQuery
          : getActiveTestsQuery;
  const adminTests = activeQuery.data?.tests || [];
  const totalAdminTests = activeQuery.data?.total || 0;
  const isLoadingAdminTests = activeQuery.isLoading;
  const adminTestsError = activeQuery.error;
  const refetchAdminTests = activeQuery.refetch;

  return {
    // Get all tests for admin (based on active tab)
    adminTests,
    totalAdminTests,
    isLoadingAdminTests,
    adminTestsError,
    refetchAdminTests,

    // Get user's own tests
    userTests:
      options?.tab === "archived"
        ? getUserArchivedTestsQuery.data?.tests || []
        : getAllUserTestsQuery.data?.tests || [],
    totalUserTests:
      options?.tab === "archived"
        ? getUserArchivedTestsQuery.data?.total || 0
        : getAllUserTestsQuery.data?.total || 0,
    isLoadingUserTests:
      options?.tab === "archived"
        ? getUserArchivedTestsQuery.isLoading
        : getAllUserTestsQuery.isLoading,
    userTestsError:
      options?.tab === "archived"
        ? getUserArchivedTestsQuery.error
        : getAllUserTestsQuery.error,
    refetchUserTests:
      options?.tab === "archived"
        ? getUserArchivedTestsQuery.refetch
        : getAllUserTestsQuery.refetch,

    // Create test
    createTest: createTestMutation.mutateAsync,
    isCreatingTest: createTestMutation.isPending,
    createTestError: createTestMutation.error,
    createTestSuccess: createTestMutation.isSuccess,

    // Update test status
    updateTestStatus: updateTestStatusMutation.mutateAsync,
    isUpdatingTestStatus: updateTestStatusMutation.isPending,
    updateTestStatusError: updateTestStatusMutation.error,
    updateTestStatusSuccess: updateTestStatusMutation.isSuccess,

    // Update test
    updateTest: updateTestMutation.mutateAsync,
    isUpdatingTest: updateTestMutation.isPending,
    updateTestError: updateTestMutation.error,
    updateTestSuccess: updateTestMutation.isSuccess,

    // Submit test
    submitTest: submitTestMutation.mutateAsync,
    isSubmittingTest: submitTestMutation.isPending,
    submitTestError: submitTestMutation.error,
    submitTestSuccess: submitTestMutation.isSuccess,

    // Update vehicle test images
    updateVehicleTestImages: updateVehicleTestImagesMutation.mutateAsync,
    isUpdatingVehicleTestImages: updateVehicleTestImagesMutation.isPending,
    updateVehicleTestImagesError: updateVehicleTestImagesMutation.error,
    updateVehicleTestImagesSuccess: updateVehicleTestImagesMutation.isSuccess,
    invalidateTestImages,

    // Get test by ID
    useGetTestById,
    getTestByIdMutation: getTestByIdMutation.mutateAsync,
    isGettingTestById: getTestByIdMutation.isPending,
    getTestByIdError: getTestByIdMutation.error,
    getTestByIdSuccess: getTestByIdMutation.isSuccess,

    // Get vehicle basic information by LPN
    getVehicleBasicInformationByLpn:
      getVehicleBasicInformationByLpnMutation.mutateAsync,
    isGettingVehicleBasicInformationByLpn:
      getVehicleBasicInformationByLpnMutation.isPending,
    getVehicleBasicInformationByLpnError:
      getVehicleBasicInformationByLpnMutation.error,
    getVehicleBasicInformationByLpnSuccess:
      getVehicleBasicInformationByLpnMutation.isSuccess,

    // Get vehicle basic information by VIN
    getVehicleBasicInformationByVin:
      getVehicleBasicInformationByVinMutation.mutateAsync,
    isGettingVehicleBasicInformationByVin:
      getVehicleBasicInformationByVinMutation.isPending,
    getVehicleBasicInformationByVinError:
      getVehicleBasicInformationByVinMutation.error,
    getVehicleBasicInformationByVinSuccess:
      getVehicleBasicInformationByVinMutation.isSuccess,

    // Add car test image
    addCarTestImage: addCarTestImageMutation.mutateAsync,
    isAddingCarTestImage: addCarTestImageMutation.isPending,
    addCarTestImageError: addCarTestImageMutation.error,
    addCarTestImageSuccess: addCarTestImageMutation.isSuccess,

    // Remove car test image
    removeCarTestImage: removeCarTestImageMutation.mutateAsync,
    isRemovingCarTestImage: removeCarTestImageMutation.isPending,
    removeCarTestImageError: removeCarTestImageMutation.error,
    removeCarTestImageSuccess: removeCarTestImageMutation.isSuccess,

    // Archive car tests
    archiveCarTests: archiveCarTestsMutation.mutateAsync,
    isArchivingCarTests: archiveCarTestsMutation.isPending,
    archiveCarTestsError: archiveCarTestsMutation.error,
    archiveCarTestsSuccess: archiveCarTestsMutation.isSuccess,

    // Unarchive car tests
    unarchiveCarTests: unarchiveCarTestsMutation.mutateAsync,
    isUnarchivingCarTests: unarchiveCarTestsMutation.isPending,
    unarchiveCarTestsError: unarchiveCarTestsMutation.error,
    unarchiveCarTestsSuccess: unarchiveCarTestsMutation.isSuccess,

    // Withdraw pending review
    withdrawPendingReview: withdrawPendingReviewMutation.mutateAsync,
    isWithdrawingPendingReview: withdrawPendingReviewMutation.isPending,
    withdrawPendingReviewError: withdrawPendingReviewMutation.error,

    // Request test change
    requestTestChange: requestTestChangeMutation.mutateAsync,
    isRequestingTestChange: requestTestChangeMutation.isPending,
    requestTestChangeError: requestTestChangeMutation.error,

    // Archived tests (for both admin and user)
    archivedTests:
      options?.tab === "archived"
        ? options?.disableAdminQuery
          ? getUserArchivedTestsQuery.data?.tests || []
          : getArchivedTestsQuery.data?.tests || []
        : [],
    isLoadingArchivedTests:
      options?.tab === "archived"
        ? options?.disableAdminQuery
          ? getUserArchivedTestsQuery.isLoading
          : getArchivedTestsQuery.isLoading
        : false,
    refetchArchivedTests:
      options?.tab === "archived"
        ? options?.disableAdminQuery
          ? getUserArchivedTestsQuery.refetch
          : getArchivedTestsQuery.refetch
        : () => Promise.resolve({} as any),

    // Reset functions
    resetCreateTest: createTestMutation.reset,
    resetUpdateStatusTest: updateTestStatusMutation.reset,
    resetUpdateTest: updateTestMutation.reset,
    resetUpdateVehicleTestImages: updateVehicleTestImagesMutation.reset,
  };
};
